<template>
	<SideNavLayout>
		<div class="admin-layout">
			<div class="top">
				<header class="header">
					<div class="titles">
						<h1>{{ $t("adminPage.title") }}</h1>
						<p>{{ $t("adminPage.subtitle") }}</p>
					</div>
					<Button shape="pill" v-if="dashboardUrl" variant="external" size="m" :href="dashboardUrl" target="_blank">
						Dashboard
					</Button>
				</header>
				<ArkBaseTabs class="tabs" variant="line" :items="tabs" :model-value="activeTab" @update:model-value="openTab" />
			</div>
			<div class="content">
				<router-view />
			</div>
		</div>
	</SideNavLayout>
</template>

<script setup lang="ts">
import { computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { useMediaQuery } from "@vueuse/core"
import SideNavLayout from "@/components/layout/SideNavLayout.vue"
import ArkBaseTabs from "@/components/ui/ArkBaseTabs.vue"
import Button from "@/components/ui/Button.vue"
import { Permission, UseRootStore } from "@/model/RootStore"
import { Route } from "@/utils/routing"
import { useAdminUserStore } from "@/stores/adminUserStore"

const route = useRoute()
const router = useRouter()
const store = UseRootStore()
const adminUserStore = useAdminUserStore()
const { t } = useI18n()
const isMobile = useMediaQuery("(max-width: 768px)")

const tabs = computed(() => {
	const user = store.currentUser
	return [
		{
			id: Route.AdminPermissions,
			label: t("adminPage.tabs.permissions"),
			visible: user?.has(Permission.AuthorizationRead) || user?.has(Permission.AuthorizationWrite),
		},
		{
			id: Route.AdminUsers,
			label: t("adminPage.tabs.users"),
			count: adminUserStore.users.length || undefined,
			visible: user?.has(Permission.AuthorizationWrite),
		},
		{
			id: Route.AdminEmailTemplates,
			label: t(isMobile.value ? "adminPage.tabs.emailTemplatesShort" : "adminPage.tabs.emailTemplates"),
			visible: user?.has(Permission.EmailCreate),
		},
		{
			id: Route.AdminServiceTokens,
			label: t(isMobile.value ? "adminPage.tabs.serviceTokensShort" : "adminPage.tabs.serviceTokens"),
			visible: user?.has(Permission.ServiceTokenWrite),
		},
	]
		.filter(tab => tab.visible)
		.map(({ id, label, count }) => ({ id, label, count }))
})

watch(
	() => store.currentUser,
	currentUser => {
		if (!currentUser?.has(Permission.AuthorizationWrite)) return
		adminUserStore.load().catch(error => console.error("Failed to load users:", error))
	},
	{ immediate: true }
)

const dashboardUrl = computed(() => {
	if (!store.currentUser?.has(Permission.DashboardRead)) return undefined
	const configured = import.meta.env.VITE_DASHBOARD_URL as string | undefined
	if (configured) return configured.replace(/\/$/, "")
	if (import.meta.env.DEV) return "http://localhost:3000"
	return undefined
})

const activeTab = computed(() => tabs.value.find(tab => tab.id === route.name)?.id)

function openTab(name: string): void {
	router.push({ name })
}

watch(
	[() => route.name, () => store.currentUser, tabs],
	([routeName, currentUser, availableTabs]) => {
		if (routeName !== Route.Admin || !currentUser) return

		const [firstTab] = availableTabs
		if (firstTab) {
			router.replace({ name: firstTab.id })
		} else if (!dashboardUrl.value) {
			router.replace({ name: Route.Home })
		}
	},
	{ immediate: true }
)
</script>

<style scoped>
.admin-layout {
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 0 var(--spacing-xl) var(--spacing-xl);
	box-sizing: border-box;

	.content {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;
	}

	.top {
		position: sticky;
		top: 0;
		z-index: 20;
		padding-top: var(--spacing-xl);
		margin-bottom: var(--spacing-lg);
		background: var(--neutral-0);
	}

	.header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: var(--spacing-md);
		margin-bottom: var(--spacing-lg);

		h1 {
			margin: 0 0 var(--spacing-xs);
			font-size: var(--font-size-2xl);
			font-weight: var(--font-weight-semibold);
			color: var(--neutral-900);
		}

		p {
			margin: 0;
			font-size: var(--font-size-base);
			color: var(--neutral-600);
		}
	}
}

.tabs :deep([data-part="list"]) {
	flex-wrap: wrap;
	gap: var(--spacing-sm) var(--spacing-lg);
}

.tabs :deep([data-part="trigger"]),
.tabs :deep([data-part="trigger"][aria-selected="true"]) {
	flex: none;
	width: auto;
	padding: 0 0 var(--spacing-smd);
	white-space: nowrap;
}

.tabs :deep(.tab-count) {
	font-weight: var(--font-weight-semibold);
}

.tabs :deep([data-part="trigger"][aria-selected="true"] .tab-count) {
	background-color: color-mix(in srgb, var(--primary-green) 12%, transparent);
	color: var(--primary-green);
}

@media (max-width: 768px) {
	.admin-layout {
		height: auto;
		padding: 0 var(--spacing-md) var(--spacing-md);

		:deep(.btn) {
			min-width: fit-content;
		}

		.top {
			padding-top: var(--spacing-md);
			margin-bottom: var(--spacing-md);
		}

		.header {
			align-items: center;
			margin-bottom: var(--spacing-md);

			h1 {
				margin: 0;
			}

			p {
				display: none;
			}
		}
	}
}

@media (max-width: 768px) {
	.tabs :deep([data-part="list"]) {
		gap: var(--spacing-sm) var(--spacing-md);
	}
}
</style>
